export const MEDIA_TYPE_OCTET_STREAM = "application/octet-stream";

const CHAT_ATTACHMENT_PROVIDER_MEDIA_TYPE_ALIASES = new Map([
  ["application/x-sh", "text/x-sh"],
  ["application/xml", "text/xml"],
  ["audio/mpeg", "audio/mp3"],
  ["text/x-java-source", "text/x-java"],
  ["text/yaml", "text/x-yaml"],
  ["video/x-msvideo", "video/avi"],
]);

const EXTENSION_TO_MEDIA_TYPE = new Map<string, string>([
  // spell-checker:disable
  // Text & code
  ["txt", "text/plain"],
  ["md", "text/markdown"],
  ["html", "text/html"],
  ["css", "text/css"],
  ["js", "application/javascript"],
  ["ts", "application/typescript"],
  ["graphql", "application/graphql"],
  ["gql", "application/graphql"],
  ["json5", "application/json5"],
  ["ndjson", "application/x-ndjson"],
  ["toml", "application/toml"],
  ["proto", "application/x-protobuf"],
  ["sql", "application/x-sql"],
  ["tf", "application/x-terraform"],
  ["hcl", "application/x-terraform"],
  ["scala", "application/x-scala"],
  ["rs", "application/x-rust"],
  ["ps1", "application/x-powershell"],
  ["patch", "application/x-patch"],
  ["diff", "application/x-patch"],
  ["php", "application/x-php"],
  ["awk", "application/x-awk"],
  ["srt", "application/x-subrip"],
  ["iif", "application/x-iif"],
  ["json", "application/json"],
  ["xml", "application/xml"],
  ["csv", "text/csv"],
  ["eml", "message/rfc822"],
  ["yaml", "text/yaml"],
  ["yml", "text/yaml"],
  ["py", "text/x-python"],
  ["java", "text/x-java-source"],
  ["c", "text/x-c"],
  ["cpp", "text/x-c++"],
  ["sh", "application/x-sh"],

  // Images
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["png", "image/png"],
  ["gif", "image/gif"],
  ["webp", "image/webp"],
  ["svg", "image/svg+xml"],
  ["ico", "image/x-icon"],

  // Audio
  ["mp3", "audio/mpeg"],
  ["wav", "audio/wav"],
  ["ogg", "audio/ogg"],
  ["m4a", "audio/mp4"],

  // Video
  ["mp4", "video/mp4"],
  ["mov", "video/quicktime"],
  ["webm", "video/webm"],
  ["avi", "video/x-msvideo"],
  ["mkv", "video/x-matroska"],

  // Docs
  ["pdf", "application/pdf"],
  ["rtf", "application/rtf"],
  ["doc", "application/msword"],
  ["docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["docm", "application/vnd.ms-word.document.macroenabled.12"],
  ["dotm", "application/vnd.ms-word.template.macroenabled.12"],
  ["dotx", "application/vnd.openxmlformats-officedocument.wordprocessingml.template"],
  ["xls", "application/vnd.ms-excel"],
  ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["xlsm", "application/vnd.ms-excel.sheet.macroenabled.12"],
  ["xltm", "application/vnd.ms-excel.template.macroenabled.12"],
  ["xltx", "application/vnd.openxmlformats-officedocument.spreadsheetml.template"],
  ["xlsb", "application/vnd.ms-excel.sheet.binary.macroenabled.12"],
  ["xlam", "application/vnd.ms-excel.addin.macroenabled.12"],
  ["ppt", "application/vnd.ms-powerpoint"],
  ["pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  ["pptm", "application/vnd.ms-powerpoint.presentation.macroenabled.12"],
  ["potm", "application/vnd.ms-powerpoint.template.macroenabled.12"],
  ["potx", "application/vnd.openxmlformats-officedocument.presentationml.template"],
  ["ppsm", "application/vnd.ms-powerpoint.slideshow.macroenabled.12"],
  ["ppsx", "application/vnd.openxmlformats-officedocument.presentationml.slideshow"],
  ["ppam", "application/vnd.ms-powerpoint.addin.macroenabled.12"],
  ["odt", "application/vnd.oasis.opendocument.text"],
  ["pages", "application/vnd.apple.pages"],
  ["key", "application/vnd.apple.keynote"],

  // Archives
  ["zip", "application/zip"],
  ["tar", "application/x-tar"],
  ["rar", "application/vnd.rar"],
  ["7z", "application/x-7z-compressed"],
  ["gz", "application/gzip"],
  // spell-checker:enable
]);

const getExtensionFromFilename = (filename: string) => filename.split(".").pop()?.trim()?.toLowerCase() ?? "";

export const normalizeMediaType = (mediaType: string) => mediaType.split(";", 1)[0]?.trim()?.toLowerCase() ?? "";

export const normalizeChatAttachmentMediaType = (mediaType: string) => {
  const normalizedMediaType = normalizeMediaType(mediaType);
  return CHAT_ATTACHMENT_PROVIDER_MEDIA_TYPE_ALIASES.get(normalizedMediaType) ?? normalizedMediaType;
};

export const getMediaTypeFromFilename = (filename: string) =>
  EXTENSION_TO_MEDIA_TYPE.get(getExtensionFromFilename(filename));

export const getResolvedFileMediaType = (file: File) => {
  const normalizedType = normalizeMediaType(file.type);
  if (normalizedType && normalizedType !== MEDIA_TYPE_OCTET_STREAM) {
    return normalizedType;
  }
  return getMediaTypeFromFilename(file.name) || normalizedType || MEDIA_TYPE_OCTET_STREAM;
};
